/**
 * One schedule, with what it will really do.
 *
 * "Active" is a state of the schedule, not a promise that mail goes out: the
 * deployment's switches and the approved-recipient list can each stop it. This
 * card says which, per schedule, from the server's own answer.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Bell, BellRing, CalendarClock, Eye, Pause, Pencil, Play } from 'lucide-react'
import type { ReportSchedule } from '@/pages/pipeline-intelligence/types'
import { reportSchedulesService, scheduleInput } from '@/services/reportSchedules'
import { formatWhen } from './format'
import type { ScheduleHealth } from './scheduleHealth'
import { Btn, Dot, Eyebrow, KeyValue, Panel, Pill } from './ui'
import type { ReportPermissions } from './useReportingData'
import type { ReportActions } from './useReportActions'

const DELIVERY_LABELS: Record<string, string> = {
  none: 'Retain only, no email',
  reviewer: 'Email one reviewer',
  team: 'Email the team',
}

function formatLabel(format: string): string {
  if (format === 'xlsx') return 'Excel'
  if (format === 'digest') return 'PDF, with the digest in the email'
  return 'PDF'
}

const NO_EDIT = 'Your role can view and run reports but not change them.'
const NO_APPROVE = 'Activating a schedule needs the approve permission.'
const NO_RUN = 'Your role cannot run reports.'

export function ScheduleCard({
  schedule,
  health,
  actions,
  perms,
  me,
  now,
  onEdit,
}: {
  schedule: ReportSchedule
  health: ScheduleHealth
  actions: ReportActions
  perms: ReportPermissions
  me: string | null
  now: Date
  onEdit: (schedule: ReportSchedule) => void
}) {
  const [previewing, setPreviewing] = useState(false)
  const preview = useMutation({
    mutationFn: () => reportSchedulesService.previewEmail(scheduleInput(schedule)),
  })
  // The preview is built from the schedule's inputs (recipients, email body, format and the rest).
  // The card stays mounted across an edit, so a preview made for the old inputs is dropped, and one
  // that is open is made again for the new ones, rather than describing mail the schedule no longer sends.
  const { mutate: makePreview, reset: dropPreview } = preview
  const inputsKey = JSON.stringify(scheduleInput(schedule))
  const previewedFor = useRef(inputsKey)
  useEffect(() => {
    if (previewedFor.current === inputsKey) return
    previewedFor.current = inputsKey
    dropPreview()
    if (previewing) makePreview()
  }, [inputsKey, previewing, dropPreview, makePreview])
  const zone = schedule.timezone || undefined
  const subscribed = Boolean(me) && schedule.recipients.some((to) => to.toLowerCase() === (me ?? '').toLowerCase())
  const busy =
    actions.toggleSchedule.isPending ||
    actions.runSchedule.isPending ||
    (actions.subscribe.isPending && actions.subscribe.variables?.id === schedule.id)
  const toggleAllowed = schedule.isActive ? perms.canEdit : perms.canApprove
  const blockers = schedule.deliveryBlockers ?? []

  return (
    <Panel as="section" aria-label={`Schedule ${schedule.name}`} className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <Eyebrow>SCHEDULE</Eyebrow>
        <span className="inline-flex items-center gap-2 text-[12px] text-[#C9C9C9]">
          <Dot tone={health.tone} />
          {health.label}
        </span>
      </div>
      <div className="text-[15px] font-semibold text-[#F5F3EE]">{schedule.name}</div>
      <p className="text-[12.5px] text-[#B9B9B9]">{health.detail}</p>

      <dl className="flex flex-col gap-2">
        <KeyValue label="Next run">{health.nextRunText}</KeyValue>
        <KeyValue label="Last run">
          {schedule.lastRunAt ? formatWhen(schedule.lastRunAt, { now, timeZone: zone, withZone: true }) : 'Never'}
        </KeyValue>
        <KeyValue label="File">{formatLabel(String(schedule.format))}</KeyValue>
        <KeyValue label="Delivery">{DELIVERY_LABELS[schedule.deliveryMode] ?? schedule.deliveryMode}</KeyValue>
      </dl>

      {schedule.deliveryMode !== 'none' ? (
        <div className="flex flex-col gap-2 border-t border-[#1B1B1B] pt-3">
          <Eyebrow>RECIPIENTS</Eyebrow>
          {schedule.recipients.length === 0 ? (
            <p className="text-[12.5px] text-[#8F8F8F]">No recipient is named.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {schedule.recipients.map((address) => {
                const approved = schedule.recipientApproval?.[address.toLowerCase()]
                return (
                  <li key={address} className="flex items-center gap-2 text-[12.5px]">
                    <span className="min-w-0 flex-1 break-all text-[#ECEAE4]">{address}</span>
                    {approved === undefined ? null : approved ? (
                      <Pill tone="ok">Approved</Pill>
                    ) : (
                      <Pill tone="warn">Not approved</Pill>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          <p className="text-[11.5px] text-[#8C8C8C]">
            Only approved recipients receive scheduled email. The approved list is set by an administrator.
          </p>
        </div>
      ) : null}

      {schedule.willEmail === true ? (
        <p className="rounded-lg border border-[#1F3A2B] bg-[#0B1410] px-3 py-2 text-[12.5px] text-[#9FE0B8]">
          Nothing stands in the way: a due run will be emailed to the recipients above.
        </p>
      ) : blockers.length > 0 ? (
        <Panel tone="bad" className="flex flex-col gap-1.5 p-3">
          <Eyebrow tone="bad">WHY NOTHING IS BEING SENT</Eyebrow>
          <ol className="flex flex-col">
            {blockers.map((blocker, index) => (
              <li key={blocker.code} className="flex gap-2.5 border-t border-[#2A1C12] py-1.5 first:border-t-0 text-[12.5px]">
                <span aria-hidden className="shrink-0 text-[#F0B08A]">{index + 1}</span>
                <span className="text-[#E8D3C4]">{blocker.label}</span>
              </li>
            ))}
          </ol>
        </Panel>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Btn
          size="sm"
          icon={schedule.isActive ? <Pause className="h-3.5 w-3.5" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
          disabled={!toggleAllowed || busy}
          title={toggleAllowed ? undefined : schedule.isActive ? NO_EDIT : NO_APPROVE}
          onClick={() => actions.toggleSchedule.mutate(schedule)}
          aria-label={`${schedule.isActive ? 'Pause' : 'Activate'} ${schedule.name}`}
        >
          {schedule.isActive ? 'Pause schedule' : 'Activate'}
        </Btn>
        <Btn
          size="sm"
          icon={<CalendarClock className="h-3.5 w-3.5" aria-hidden />}
          disabled={!perms.canRun || busy}
          title={perms.canRun ? 'Make a preview now. Nothing is emailed.' : NO_RUN}
          onClick={() => actions.runSchedule.mutate(schedule)}
          aria-label={`Run ${schedule.name} now`}
        >
          Run schedule now
        </Btn>
        <Btn
          size="sm"
          icon={<Eye className="h-3.5 w-3.5" aria-hidden />}
          aria-expanded={previewing}
          onClick={() => {
            const next = !previewing
            setPreviewing(next)
            if (next && !preview.data) preview.mutate()
          }}
        >
          {previewing ? 'Hide email preview' : 'Preview the email'}
        </Btn>
        <Btn
          size="sm"
          icon={<Pencil className="h-3.5 w-3.5" aria-hidden />}
          disabled={!perms.canEdit}
          title={perms.canEdit ? 'Changing a schedule pauses it until it is activated again.' : NO_EDIT}
          onClick={() => onEdit(schedule)}
          aria-label={`Edit schedule ${schedule.name}`}
        >
          Edit schedule
        </Btn>
        {me ? (
          <Btn
            size="sm"
            icon={subscribed ? <BellRing className="h-3.5 w-3.5" aria-hidden /> : <Bell className="h-3.5 w-3.5" aria-hidden />}
            disabled={!perms.canEdit || busy}
            title={perms.canEdit ? 'Adds or removes your address. This pauses the schedule until it is activated again.' : NO_EDIT}
            onClick={() => actions.subscribe.mutate(schedule)}
            aria-label={`${subscribed ? 'Unsubscribe from' : 'Subscribe to'} ${schedule.name}`}
          >
            {subscribed ? 'Unsubscribe me' : 'Subscribe me'}
          </Btn>
        ) : null}
      </div>

      {previewing ? (
        <div role="region" aria-label="Email preview" className="rounded-lg border border-[#2A2A2A] bg-[#0A0A0A] p-3 text-[12.5px] text-[#D8D8D8]">
          {preview.isPending ? (
            <p className="text-[#8F8F8F]">Rendering the email…</p>
          ) : preview.isError ? (
            <p role="alert" className="text-[#F0B08A]">The email preview could not be rendered.</p>
          ) : preview.data ? (
            <>
              <div className="mb-1.5 text-[11.5px] text-[#8F8F8F]">Subject: {preview.data.subject}</div>
              <p className="whitespace-pre-line">{preview.data.bodyText}</p>
              <p className="mt-2 text-[11.5px] text-[#8C8C8C]">Preview only. Nothing was sent.</p>
            </>
          ) : null}
        </div>
      ) : null}
    </Panel>
  )
}
